/**
 * Extract up to `max` uppercase initials from a display name.
 *
 * - Collapses runs of whitespace
 * - Uses first letter of the first and last parts when `max >= 2`
 * - Returns `fallback` (default `'?'`) for empty/whitespace-only input
 */
export function getInitials(
  name: string | null | undefined,
  options: { max?: number; fallback?: string } = {},
): string {
  const { max = 2, fallback = '?' } = options;
  if (!name) return fallback;

  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return fallback;

  if (max <= 1 || parts.length === 1) {
    return (parts[0]?.[0] ?? fallback).toUpperCase();
  }

  const first = parts[0]?.[0] ?? '';
  const last = parts[parts.length - 1]?.[0] ?? '';
  const initials = `${first}${last}`.toUpperCase();
  return initials.length > 0 ? initials : fallback;
}

export interface UserInitialsSource {
  name?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
}

/** Split a full display name into first / last segments (best-effort). */
export function splitDisplayName(full: string): {
  firstName: string;
  lastName: string;
  displayName: string;
} {
  const parts = full.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return { firstName: '', lastName: '', displayName: '' };
  }
  if (parts.length === 1) {
    const only = parts[0] ?? '';
    return { firstName: only, lastName: '', displayName: only };
  }
  return {
    firstName: parts[0] ?? '',
    lastName: parts.slice(1).join(' '),
    displayName: parts.join(' '),
  };
}

function firstChar(value: string | undefined): string {
  const trimmed = value?.trim();
  if (!trimmed) {
    return '';
  }
  const ch = trimmed[0];
  if (!ch) {
    return '';
  }
  return /[A-Za-zÀ-ÿ]/.test(ch) ? ch.toUpperCase() : '';
}

function twoLetterInitials(first?: string, second?: string): string {
  const a = firstChar(first);
  const b = firstChar(second);
  if (a && b) return `${a}${b}`;
  return a || b || '';
}

/**
 * Initials for avatars: uses structured first/last name when available, then
 * display name, then e-mail local part. Never returns more than `max` letters.
 */
export function getUserInitials(
  user: UserInitialsSource | null | undefined,
  options: { max?: number; fallback?: string } = {},
): string {
  const { max = 2, fallback = '?' } = options;
  return computeAvatarInitials(user, { max, fallback });
}

/**
 * Strict avatar letters (max 2, A–Z only). Never returns a raw word like "you".
 */
export function computeAvatarInitials(
  user: UserInitialsSource | null | undefined,
  options: { max?: number; fallback?: string } = {},
): string {
  const { max = 2, fallback = '?' } = options;
  if (!user) return fallback;

  const cap = Math.min(Math.max(max, 1), 2);

  const first = user.firstName?.trim() ?? '';
  const last = user.lastName?.trim() ?? '';
  let letters = '';

  if (first && last) {
    letters = twoLetterInitials(first, last);
  } else if (first) {
    letters = first.length >= 2 ? first.slice(0, cap).toUpperCase() : firstChar(first);
  } else if (last) {
    letters = last.length >= 2 ? last.slice(0, cap).toUpperCase() : firstChar(last);
  }

  if (!letters && user.name?.trim()) {
    const parts = user.name.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      letters = twoLetterInitials(parts[0], parts[parts.length - 1]);
    } else if (parts[0]) {
      const word = parts[0];
      letters =
        word.length >= 2
          ? word.slice(0, cap).toUpperCase()
          : firstChar(word);
    }
  }

  if (!letters && user.email) {
    const local = user.email.split('@')[0]?.trim() ?? '';
    const segments = local.split(/[._-]+/).filter(Boolean);
    if (segments.length >= 2) {
      letters = twoLetterInitials(segments[0], segments[segments.length - 1]);
    } else if (segments[0]) {
      const word = segments[0];
      letters =
        word.length >= 2
          ? word.slice(0, cap).toUpperCase()
          : firstChar(word);
    }
  }

  const safe = letters.replace(/[^A-Z]/g, '').slice(0, cap);
  return safe || fallback;
}
